import React from 'react';
import { DataIndicators } from '../../../../../components/DataIndicators';
import { useLanguageLocale } from '../../../../../hooks/language-locale-hooks';
import { isDataLoaded } from '../../../../../types/data-providers';
import { GenericToolbar } from '../../../../../components/GenericToolbar';
import { pushAppRoute } from '../../../../../data/domain/route-manager';
import { useSelectedSiteData, isRecordFound } from '../../../../../hooks/data-hooks';
import { useHistory } from 'react-router-dom';
import { ContentContainer } from '../../../../../components/ContentContainer';
import { RecordDataIndicators } from '../../../../../components/RecordDataIndicators';
import { useSiteDevicesData } from '../../../../../data/DataProviders/SiteDevicesDataProvider';
import { useAppRoute } from '../../../../../hooks/app-route-hooks';
import { DeviceListGroups } from '../../../../../components/DeviceListGroups';
import { DeviceListItem } from '../../../../../components/DeviceListItem';
import { sortObjectArrayNaturally, compareNaturally } from '../../../../../lib';
import _ from 'lodash';
import { DeviceListData } from '../../../../../types/device';

export const OverviewSiteDevices: React.FC = () => {
    const selectedSiteData = useSelectedSiteData();
    const currentAppRoute = useAppRoute('OverviewSiteDevices');
    const history = useHistory();
    const { t } = useLanguageLocale();
    const siteDevicesData = useSiteDevicesData();

    const navigateToDeviceDetail = (deviceData: { id: string }): void => {
        pushAppRoute(history, {
            type: 'OverviewSiteDevicesDeviceDetails',
            siteId: currentAppRoute.siteId,
            locationId: currentAppRoute.locationId,
            deviceId: deviceData.id,
        });
    };

    const devices: DeviceListData[] = isDataLoaded(siteDevicesData)
        ? sortObjectArrayNaturally(siteDevicesData.value, [{ sortField: 'type' }])
        : [];

    const deviceGroups = isDataLoaded(siteDevicesData)
        ? _.groupBy(devices, (d) => d.type || t('DASHBOARD.UNKNOWN_DEVICE_TYPE'))
        : {};

    const groups = Object.keys(deviceGroups)
        .sort(compareNaturally)
        .map((groupName) => ({
            name: groupName,
            items: deviceGroups[groupName],
        }));
    const isEmpty = isDataLoaded(siteDevicesData) && siteDevicesData.value.length === 0;

    return (
        <>
            <GenericToolbar
                showBackButton
                backButtonOnClick={(): void => {
                    pushAppRoute(history, {
                        type: 'OverviewSite',
                        locationId: currentAppRoute.locationId,
                        siteId: currentAppRoute.siteId,
                    });
                }}
                title={t('DASHBOARD.SITE_DETAILS.ALL_DEVICES')}
                subtitle={isRecordFound(selectedSiteData) ? selectedSiteData.value.name : ''}
            />

            <ContentContainer testId="site-details-devices-page" justifyContent="simple-center">
                <RecordDataIndicators
                    recordData={selectedSiteData}
                    errorMessage={t('DASHBOARD.ERROR_LOADING_SITE')}
                    notFoundIcon="Business"
                    notFoundMessage={t('DASHBOARD.SITE_NOT_FOUND')}
                />

                {isRecordFound(selectedSiteData) && (
                    <>
                        <DataIndicators
                            errorMessage={t('DASHBOARD.ERROR_LOADING_DATA')}
                            emptyMessage={t('DASHBOARD.NO_DATA')}
                            data={siteDevicesData}
                            emptyIcon="Devices"
                            isEmpty={isEmpty}
                        />

                        {isDataLoaded(siteDevicesData) && !isEmpty && (
                            <div data-testid="site-devices-card">
                                <DeviceListGroups
                                    groups={groups}
                                    component={DeviceListItem}
                                    navigationFn={navigateToDeviceDetail}
                                />
                            </div>
                        )}
                    </>
                )}
            </ContentContainer>
        </>
    );
};
